import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createTicket, getTicketCounts, getTicketSources, listDepartments, listPriorities, listStatuses, listTickets, listTicketTypes, listTicketCategories, listTicketTemplates, listAssignees, updateTicket, uploadTicketAttachment } from "../lib/api";
import { LocationPicker } from "../components/LocationPicker";
import { AssigneesField, RequestersField, type AssigneePick, type RequesterPick } from "../components/PersonPickers";
import { getCurrentUser } from "../lib/auth";
import { useApi, useMutation } from "../lib/useApi";
import { useFavorites } from "../lib/useFavorites";
import { formatTicketNumber, timeAgo } from "../lib/format";
import { useTicketNumberFormat } from "../lib/ticketNumbering";
import { TICKET_PRIORITIES, TICKET_STATUSES } from "../lib/constants";
import type { TicketRow, TicketStatusRow, TicketCategoryRow } from "../lib/types";
import { RequestTypePicker } from "./TicketDetailPage";
import { PageBar } from "../components/Layout";
import { Avatar } from "../components/Avatar";
import { PriorityBadge, StatusBadge } from "../components/Badge";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { Modal } from "../components/Modal";
import { RichTextEditor, richTextIsEmpty } from "../components/RichText";
import { TicketDetailBody } from "./TicketDetailPage";

/** Left-rail quick filters. "Mine" reuses the `assignee` param; "My Group"
 *  resolves the caller's team(s) server-side via `mineTeam`. */
type QuickFilter = "all" | "mine" | "my_group" | "open" | "pending" | "favorites";

/** Inbox (list) vs Board (Kanban grouped by status). Persisted per browser. */
type TicketView = "inbox" | "board";

export function TicketsPage() {
  const navigate = useNavigate();
  const { id: selectedId } = useParams();
  const me = getCurrentUser();

  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [priority, setPriority] = useState("");
  const [department, setDepartment] = useState("");
  const [quick, setQuick] = useState<QuickFilter>("all");
  const { isFavorite } = useFavorites();
  const [showCreate, setShowCreate] = useState(false);
  // View preference (Inbox list ↔ Kanban board), remembered across visits.
  const [view, setView] = useState<TicketView>(
    () => (localStorage.getItem("ac.tickets.view") === "board" ? "board" : "inbox"),
  );
  useEffect(() => {
    localStorage.setItem("ac.tickets.view", view);
  }, [view]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Tenant-configured priorities for the filter; seeded constants while loading.
  const prioritiesApi = useApi((signal) => listPriorities(signal), []);
  const priorityOptions: { code: string; label: string }[] =
    prioritiesApi.data?.priorities?.map((p) => ({ code: p.code, label: p.name })) ??
    TICKET_PRIORITIES;

  // Tenant-configured departments (0021) for the inbox Department filter.
  const departmentsApi = useApi((signal) => listDepartments(signal), []);
  const departmentOptions = departmentsApi.data?.departments ?? [];

  // Live counts for the quick-filter chips (respect the other filters).
  const countsApi = useApi(
    (signal) =>
      getTicketCounts(
        {
          ...(debounced ? { q: debounced } : {}),
          ...(priority ? { priority } : {}),
          ...(department ? { department } : {}),
        },
        signal,
      ),
    [debounced, priority, department],
  );
  const counts = countsApi.data?.counts;

  // Quick filters map onto the list endpoint's real params (status / assignee /
  // mineTeam). "My Group" is resolved to the caller's teams server-side.
  const status = quick === "open" ? "open" : quick === "pending" ? "pending" : "";
  const assignee = quick === "mine" ? me?.id ?? "" : "";
  const mineTeam = quick === "my_group";

  const { data, loading, error, reload } = useApi(
    (signal) =>
      listTickets(
        {
          ...(status ? { status } : {}),
          ...(priority ? { priority } : {}),
          ...(assignee ? { assignee } : {}),
          ...(mineTeam ? { mineTeam: true } : {}),
          ...(debounced ? { q: debounced } : {}),
          ...(department ? { department } : {}),
        },
        signal,
      ),
    [status, priority, assignee, mineTeam, debounced, department],
  );

  // Favorites is a client-side lens over the loaded list (server filters stay
  // neutral for it, so the full set is available to filter down to the stars).
  const allTickets = data?.tickets ?? [];
  const tickets =
    quick === "favorites" ? allTickets.filter((t) => isFavorite("ticket", t.id)) : allTickets;
  const hasSelection = !!selectedId;

  function openTicket(id: string) {
    navigate(`/tickets/${id}`);
  }

  // Board cards open the ticket in a dialog (overlay) so the board stays put;
  // the inbox list still opens into its reading pane via openTicket.
  const [dialogId, setDialogId] = useState<string | null>(null);

  const filtersActive = !!(debounced || priority || department || quick !== "all");

  return (
    <>
      <PageBar
        crumbs="Tickets"
        actions={
          <>
            <div className="seg" role="group" aria-label="Ticket view">
              <button
                type="button"
                className={`seg-btn${view === "inbox" ? " active" : ""}`}
                aria-pressed={view === "inbox"}
                onClick={() => setView("inbox")}
              >
                List
              </button>
              <button
                type="button"
                className={`seg-btn${view === "board" ? " active" : ""}`}
                aria-pressed={view === "board"}
                onClick={() => {
                  setView("board");
                  // The board is full-width and only renders when nothing is
                  // selected — clear any open ticket so the switch takes effect.
                  if (selectedId) navigate("/tickets");
                }}
              >
                Board
              </button>
            </div>
            <button className="btn primary" onClick={() => setShowCreate(true)}>
              + New ticket
            </button>
          </>
        }
      />
      {/* Board (Kanban) view — full-width, grouped by status, drag to change
          status. Shown only when no ticket is open; opening one falls through
          to the inbox split so the detail pane reads normally. */}
      {view === "board" && !hasSelection ? (
        <TicketBoard
          filters={{
            ...(debounced ? { q: debounced } : {}),
            ...(priority ? { priority } : {}),
            ...(department ? { department } : {}),
            ...(quick === "mine" && me?.id ? { assignee: me.id } : {}),
            ...(quick === "my_group" ? { mineTeam: true } : {}),
          }}
          onOpen={setDialogId}
        />
      ) : (
      /* `has-selection` switches the narrow layout to detail-only (the list
          collapses); at wide widths both panes always show. */
      <div className={`inbox${hasSelection ? " has-selection" : ""}`}>
        <section className="inbox-list" aria-label="Ticket inbox">
          <div className="inbox-list-head">
            <div className="inbox-search">
              <span className="search-icon" aria-hidden="true">
                <SearchGlyph />
              </span>
              <input
                type="text"
                className="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search subject or number…"
                aria-label="Search tickets by subject or number"
              />
            </div>
            <div className="inbox-filters" role="group" aria-label="Quick filters">
              <FilterPill active={quick === "all"} onClick={() => setQuick("all")}>
                All{counts ? ` (${counts.all})` : ""}
              </FilterPill>
              <FilterPill
                active={quick === "mine"}
                onClick={() => setQuick("mine")}
                disabled={!me?.id}
                title={me?.id ? "Assigned to me" : "Sign-in required"}
              >
                Mine{counts ? ` (${counts.mine})` : ""}
              </FilterPill>
              <FilterPill
                active={quick === "my_group"}
                onClick={() => setQuick("my_group")}
                disabled={!me?.id}
                title={me?.id ? "Routed to my tech group(s)" : "Sign-in required"}
              >
                My Group{counts ? ` (${counts.my_group})` : ""}
              </FilterPill>
              <FilterPill active={quick === "open"} onClick={() => setQuick("open")}>
                Open{counts ? ` (${counts.open})` : ""}
              </FilterPill>
              <FilterPill active={quick === "pending"} onClick={() => setQuick("pending")}>
                Pending{counts ? ` (${counts.pending})` : ""}
              </FilterPill>
              <FilterPill
                active={quick === "favorites"}
                onClick={() => setQuick("favorites")}
                title="Tickets you've starred"
              >
                ★ Favorites
              </FilterPill>
              <select
                className="inbox-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                aria-label="Filter by priority"
              >
                <option value="">Any priority</option>
                {priorityOptions.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.label}
                  </option>
                ))}
                {priority && !priorityOptions.some((p) => p.code === priority) ? (
                  <option value={priority}>{priority}</option>
                ) : null}
              </select>
              <select
                className="inbox-priority"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                aria-label="Filter by department"
              >
                <option value="">Any department</option>
                {departmentOptions.map((d) => (
                  <option key={d.id} value={d.code}>
                    {d.name}
                  </option>
                ))}
                {department && !departmentOptions.some((d) => d.code === department) ? (
                  <option value={department}>{department}</option>
                ) : null}
              </select>
            </div>
          </div>

          <div className="inbox-list-body">
            {loading ? (
              <Loading label="Loading tickets…" />
            ) : error ? (
              <ErrorState error={error} onRetry={reload} />
            ) : tickets.length === 0 ? (
              <EmptyState>
                {filtersActive
                  ? "No tickets match the current filters."
                  : "No tickets yet. Create the first one to get started."}
              </EmptyState>
            ) : (
              <InboxList tickets={tickets} selectedId={selectedId} onOpen={openTicket} />
            )}
          </div>

          {tickets.length > 0 ? (
            <div className="inbox-list-foot muted">
              {tickets.length} ticket{tickets.length === 1 ? "" : "s"}
            </div>
          ) : null}
        </section>

        <section className="inbox-read" aria-label="Ticket detail">
          {selectedId ? (
            <div className="inbox-read-inner">
              <button
                className="btn ghost small inbox-back"
                onClick={() => navigate("/tickets")}
              >
                ← Back to list
              </button>
              <TicketDetailBody
                key={selectedId}
                id={selectedId}
                onDeleted={() => {
                  reload();
                  navigate("/tickets");
                }}
              />
            </div>
          ) : (
            <div className="inbox-read-empty">
              <InboxGlyph />
              <h2>Select a ticket</h2>
              <p className="muted">
                Pick a request from the inbox to read the conversation, update its
                status, and track its SLA.
              </p>
            </div>
          )}
        </section>
      </div>
      )}

      {showCreate ? (
        <CreateTicketModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            reload();
            navigate(`/tickets/${id}`);
          }}
        />
      ) : null}

      {dialogId ? (
        <Modal title="Ticket" onClose={() => setDialogId(null)} wide>
          <TicketDetailBody id={dialogId} onDeleted={() => { setDialogId(null); reload(); }} />
        </Modal>
      ) : null}
    </>
  );
}

// ===========================================================================
// Kanban board view — tickets grouped into status columns. Drag a card onto a
// column to change its status (PATCH status_code). The per-card <select> is the
// keyboard-accessible fallback. Reuses the project board's `.board-*` styles.
// ===========================================================================

type TicketListFilters = { q?: string; priority?: string; department?: string; assignee?: string; mineTeam?: boolean };

function TicketBoard({
  filters,
  onOpen,
}: {
  filters: TicketListFilters;
  onOpen: (id: string) => void;
}) {
  // Stable dependency key so the fetch re-runs only when a filter value changes.
  const filterKey = JSON.stringify(filters);
  const { data, loading, error, reload } = useApi(
    (signal) => listTickets(filters, signal),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey],
  );
  // Tenant-configured statuses define the columns + their order; seeded
  // constants stand in while the lookup loads (or if it fails).
  const statusesApi = useApi((signal) => listStatuses(signal), []);
  const columns: { code: string; name: string; color?: string | null }[] = useMemo(() => {
    const rows = statusesApi.data?.statuses;
    if (rows?.length) {
      return [...rows]
        .sort((a, b) => a.position - b.position)
        .map((s: TicketStatusRow) => ({ code: s.code, name: s.name, color: s.color ?? null }));
    }
    return TICKET_STATUSES.map((s) => ({ code: s.code, name: s.label }));
  }, [statusesApi.data]);
  // The /api/tickets rows carry status_id (FK), not status_code — resolve the
  // status CODE per ticket via the lookup so cards land in the right column.
  // (status_code is honoured first for any future enriched/denormalized rows.)
  const statusCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of statusesApi.data?.statuses ?? []) m.set(s.id, s.code);
    return m;
  }, [statusesApi.data]);
  const codeOf = useCallback(
    (t: TicketRow): string =>
      t.status_code || (t.status_id ? statusCodeById.get(t.status_id) : undefined) || "open",
    [statusCodeById],
  );
  // Same FK→code resolution for priority, so cards show the priority badge/rail.
  const prioritiesApi = useApi((signal) => listPriorities(signal), []);
  const priorityCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of prioritiesApi.data?.priorities ?? []) m.set(p.id, p.code);
    return m;
  }, [prioritiesApi.data]);
  const priorityCodeOf = useCallback(
    (t: TicketRow): string | null =>
      t.priority_code || (t.priority_id ? priorityCodeById.get(t.priority_id) ?? null : null),
    [priorityCodeById],
  );

  const [dragId, setDragId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const moveMut = useMutation((vars: { id: string; status_code: string }) =>
    updateTicket(vars.id, { status_code: vars.status_code }),
  );

  const handleDrop = useCallback(
    async (ticketId: string, statusCode: string, currentStatus: string) => {
      setDragId(null);
      if (statusCode === currentStatus) return;
      setMovingId(ticketId);
      await moveMut.run({ id: ticketId, status_code: statusCode });
      setMovingId(null);
      reload();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moveMut.run, reload],
  );

  if (loading) return <Loading label="Loading board…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const tickets = data?.tickets ?? [];
  const byStatus = (code: string) => tickets.filter((t) => codeOf(t) === code);
  // Any ticket whose status isn't one of the known columns lands in the first.
  const known = new Set(columns.map((c) => c.code));
  const orphanCount = tickets.filter((t) => !known.has(codeOf(t))).length;

  return (
    <div className="board-content">
      {moveMut.error ? (
        <Alert kind="error">Couldn't move the ticket: {moveMut.error}</Alert>
      ) : null}
      <div className="board">
        {columns.map((col, idx) => (
          <TicketColumn
            key={col.code}
            column={col}
            // Surface orphan-status tickets in the leftmost column so they're reachable.
            tickets={idx === 0 ? [...byStatus(col.code), ...tickets.filter((t) => !known.has(codeOf(t)))] : byStatus(col.code)}
            codeOf={codeOf}
            priorityCodeOf={priorityCodeOf}
            dragId={dragId}
            movingId={movingId}
            onDragState={setDragId}
            onDrop={handleDrop}
            onOpen={onOpen}
            note={idx === 0 && orphanCount > 0 ? `Includes ${orphanCount} with other statuses` : null}
          />
        ))}
      </div>
    </div>
  );
}

function TicketColumn({
  column,
  tickets,
  codeOf,
  priorityCodeOf,
  dragId,
  movingId,
  onDragState,
  onDrop,
  onOpen,
  note,
}: {
  column: { code: string; name: string; color?: string | null };
  tickets: TicketRow[];
  codeOf: (t: TicketRow) => string;
  priorityCodeOf: (t: TicketRow) => string | null;
  dragId: string | null;
  movingId: string | null;
  onDragState: (id: string | null) => void;
  onDrop: (ticketId: string, statusCode: string, currentStatus: string) => void;
  onOpen: (id: string) => void;
  note: string | null;
}) {
  const [dropOver, setDropOver] = useState(false);
  const depth = useRef(0);

  function handleDragEnter(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    depth.current += 1;
    setDropOver(true);
  }
  function handleDragLeave() {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDropOver(false);
  }
  function handleDragOver(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
  function handleDrop(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    depth.current = 0;
    setDropOver(false);
    const payload = e.dataTransfer.getData("text/plain");
    if (!payload) return;
    const [ticketId, currentStatus] = payload.split("|");
    if (ticketId) onDrop(ticketId, column.code, currentStatus ?? "");
  }

  // Stable accent per status code so each column reads distinctly.
  const accents = ["var(--st-open)", "var(--st-progress)", "var(--st-done)", "var(--accent)", "var(--brand-2)"];
  let h = 0;
  for (let i = 0; i < column.code.length; i++) h = (h * 31 + column.code.charCodeAt(i)) >>> 0;
  const colAccent = column.color || accents[h % accents.length];

  return (
    <section
      className={`board-col${dropOver ? " col-dropover" : ""}`}
      aria-label={column.name}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <header className="board-col-head">
        <span className="col-swatch" style={{ ["--col-accent" as string]: colAccent }} aria-hidden="true" />
        <span className="col-name">{column.name}</span>
        <span className="grow" />
        <span className="count">{tickets.length}</span>
      </header>
      {note ? <div className="board-col-wip">{note}</div> : null}
      <div className="board-col-body">
        {tickets.length === 0 ? (
          <p className="muted board-empty">No tickets</p>
        ) : (
          tickets.map((t) => (
            <TicketCard
              key={t.id}
              ticket={t}
              current={codeOf(t)}
              priorityCode={priorityCodeOf(t)}
              dragging={dragId === t.id}
              moving={movingId === t.id}
              onDragState={onDragState}
              onOpen={() => onOpen(t.id)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function TicketCard({
  ticket,
  current,
  priorityCode,
  dragging,
  moving,
  onDragState,
  onOpen,
}: {
  ticket: TicketRow;
  /** Resolved status code (from status_id via the lookup), not the raw FK. */
  current: string;
  /** Resolved priority code (from priority_id via the lookup), or null. */
  priorityCode: string | null;
  dragging: boolean;
  moving: boolean;
  onDragState: (id: string | null) => void;
  onOpen: () => void;
}) {
  const numFmt = useTicketNumberFormat();
  const rail =
    {
      urgent: "var(--st-blocked)",
      high: "var(--st-progress)",
      normal: "var(--st-open)",
      low: "var(--st-closed)",
    }[(priorityCode ?? "").toLowerCase()] ?? "var(--st-closed)";

  return (
    <article
      className={`board-card${moving ? " busy" : ""}${dragging ? " card-dragging" : ""}`}
      style={{ ["--card-rail" as string]: rail }}
      draggable
      onDragStart={(e) => {
        // Don't start a drag when the gesture begins on an interactive control
        // (the status <select>, the title button, links). A native <select>
        // inside a draggable element otherwise never opens / never fires change.
        if ((e.target as HTMLElement).closest("select, button, input, a, option")) {
          e.preventDefault();
          return;
        }
        // Pack id + current status so the column knows whether the drop is a no-op.
        e.dataTransfer.setData("text/plain", `${ticket.id}|${current}`);
        e.dataTransfer.effectAllowed = "move";
        onDragState(ticket.id);
      }}
      onDragEnd={() => onDragState(null)}
    >
      {ticket.number ? <span className="board-card-origin mono faint">{formatTicketNumber(ticket.number, numFmt)}</span> : null}
      <button className="board-card-title" onClick={onOpen} title="Open ticket">
        {ticket.subject}
      </button>
      <div className="board-card-foot">
        {priorityCode ? <PriorityBadge code={priorityCode} /> : null}
        <span className="grow" />
        {ticket.requester_name ? <span className="faint" style={{ fontSize: "0.72rem" }}>{ticket.requester_name}</span> : null}
        <Avatar name={ticket.assignee_name ?? ticket.requester_name ?? undefined} size="sm" />
      </div>
    </article>
  );
}

function FilterPill({
  active,
  onClick,
  disabled,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`inbox-filter${active ? " active" : ""}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

/** Keyboard-navigable list of compact inbox rows (↑/↓ to move, Enter to open). */
function InboxList({
  tickets,
  selectedId,
  onOpen,
}: {
  tickets: TicketRow[];
  selectedId: string | undefined;
  onOpen: (id: string) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  // Resolve the row's status_id → code (list rows carry the FK, not the code)
  // and the ticket type code → display name, so each row can show both.
  const statusesApi = useApi((signal) => listStatuses(signal), []);
  const typesApi = useApi((signal) => listTicketTypes(signal), []);
  const statusCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of statusesApi.data?.statuses ?? []) m.set(s.id, s.code);
    return m;
  }, [statusesApi.data]);
  const typeNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of typesApi.data?.ticket_types ?? []) m.set(t.code, t.name);
    return m;
  }, [typesApi.data]);
  const typeColorByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of typesApi.data?.ticket_types ?? []) if (t.color) m.set(t.code, t.color);
    return m;
  }, [typesApi.data]);

  function onKeyDown(e: KeyboardEvent<HTMLLIElement>, idx: number) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = e.key === "ArrowDown" ? idx + 1 : idx - 1;
      const rows = listRef.current?.querySelectorAll<HTMLLIElement>("li.inbox-row");
      rows?.[Math.max(0, Math.min(rows.length - 1, next))]?.focus();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen(tickets[idx]!.id);
    }
  }

  return (
    <ul className="inbox-rows" ref={listRef}>
      {tickets.map((t, idx) => (
        <InboxRow
          key={t.id}
          ticket={t}
          statusCode={t.status_code || (t.status_id ? statusCodeById.get(t.status_id) : undefined)}
          typeName={t.type ? typeNameByCode.get(t.type) ?? t.type : undefined}
          typeColor={t.type ? typeColorByCode.get(t.type) ?? null : null}
          selected={t.id === selectedId}
          onOpen={() => onOpen(t.id)}
          onKeyDown={(e) => onKeyDown(e, idx)}
        />
      ))}
    </ul>
  );
}

/** Short human label for a future/overdue SLA, e.g. "2h", "45m", "overdue". */
function slaRemaining(dueAt: number): string {
  const secs = dueAt - Math.floor(Date.now() / 1000);
  if (secs <= 0) return "overdue";
  const h = Math.floor(secs / 3600);
  if (h >= 24) return `${Math.floor(h / 24)}d`;
  if (h >= 1) return `${h}h`;
  return `${Math.max(1, Math.floor(secs / 60))}m`;
}

/** SLA status pill for a ticket row: overdue (red), due-soon (amber), or due. */
function SlaChip({ ticket }: { ticket: TicketRow }) {
  const breached = ticket.sla_breached === 1;
  const dueAt = ticket.sla_due_at ?? null;
  if (!breached && dueAt == null) return null;
  const overdue = breached || (dueAt != null && dueAt <= Math.floor(Date.now() / 1000));
  const soon = !overdue && dueAt != null && dueAt - Math.floor(Date.now() / 1000) < 2 * 3600;
  const kind = ticket.sla_kind === "first_response" ? "1st reply" : "resolve";
  const tone = overdue ? "sla-chip-overdue" : soon ? "sla-chip-soon" : "sla-chip-ok";
  const text = overdue ? "SLA overdue" : `SLA ${kind} ${dueAt != null ? slaRemaining(dueAt) : ""}`.trim();
  return (
    <span className={`pill sla-chip ${tone}`} style={{ fontSize: "0.68rem" }} title={`SLA: ${kind}`}>
      {text}
    </span>
  );
}

function InboxRow({
  ticket,
  statusCode,
  typeName,
  typeColor,
  selected,
  onOpen,
  onKeyDown,
}: {
  ticket: TicketRow;
  statusCode: string | undefined;
  typeName: string | undefined;
  typeColor?: string | null;
  selected: boolean;
  onOpen: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void;
}) {
  const requester = ticket.requester_name ?? null;
  const numFmt = useTicketNumberFormat();
  // Last-activity signal: the most recent of last reply / updated_at.
  const activityAt = useMemo(
    () => Math.max(ticket.last_reply_at ?? 0, ticket.updated_at ?? 0) || ticket.updated_at,
    [ticket.last_reply_at, ticket.updated_at],
  );

  return (
    <li
      className={`inbox-row${selected ? " selected" : ""}`}
      tabIndex={0}
      role="button"
      aria-current={selected ? "true" : undefined}
      aria-label={`Ticket: ${ticket.subject}`}
      onClick={onOpen}
      onKeyDown={onKeyDown}
    >
      <Avatar name={requester ?? ticket.assignee_name ?? undefined} size="md" />
      <div className="inbox-row-main">
        <div className="inbox-row-top">
          <span className="inbox-row-subject">{ticket.subject}</span>
          {ticket.number ? <span className="inbox-row-num mono faint">{formatTicketNumber(ticket.number, numFmt)}</span> : null}
        </div>
        <div className="inbox-row-snip">
          {requester ? <span className="inbox-row-req">{requester}</span> : null}
          {requester ? <span aria-hidden="true"> · </span> : null}
          <span className="faint">{timeAgo(activityAt)}</span>
        </div>
        {statusCode || typeName || ticket.sla_due_at || ticket.sla_breached || (ticket.tag_names ?? "").trim() ? (
          <div className="inbox-row-meta">
            {statusCode ? <StatusBadge code={statusCode} /> : null}
            {typeName ? (
              <span
                title="Type"
                className="pill"
                style={
                  typeColor
                    ? {
                        fontSize: "0.68rem",
                        color: typeColor,
                        background: `color-mix(in srgb, ${typeColor} 12%, transparent)`,
                        borderColor: `color-mix(in srgb, ${typeColor} 36%, transparent)`,
                      }
                    : { fontSize: "0.68rem" }
                }
              >
                {typeName}
              </span>
            ) : null}
            <SlaChip ticket={ticket} />
            {/* Tags share the same meta line — they wrap only if the row overflows. */}
            {(ticket.tag_names ?? "")
              .split(",")
              .filter(Boolean)
              .slice(0, 3)
              .map((t) => (
                <span key={t} title="Tag" className="pill" style={{ fontSize: "0.68rem" }}>
                  {t}
                </span>
              ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

function SearchGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function InboxGlyph() {
  return (
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 7l9 6 9-6M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Legacy-parity intake lookups (mirror the TicketDetailPage constants). */
const NEW_TICKET_TYPES: { code: string; label: string }[] = [
  { code: "service_request", label: "Service Request" },
  { code: "incident", label: "Incident" },
  { code: "problem", label: "Problem" },
];
const NEW_TICKET_SOURCES: { code: string; label: string }[] = [
  { code: "web", label: "Web" },
  { code: "email", label: "E-Mail" },
  { code: "portal", label: "Portal" },
  { code: "api", label: "API" },
  { code: "phone", label: "Phone" },
  { code: "walk_in", label: "Walk-In" },
];

function CreateTicketModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const assigneesApi = useApi((signal) => listAssignees(signal, { workType: "tickets", field: "assignee" }), []);
  const assignees = assigneesApi.data?.assignees ?? [];
  // Departments are tenant config (0021): the select stores the department CODE,
  // which the API matches against departments.code to route the ticket's team.
  const departmentsApi = useApi((signal) => listDepartments(signal), []);
  const departmentOptions = departmentsApi.data?.departments ?? [];
  // Request type cascade (Category → Sub Type → Item) — same picker the edit
  // window uses, so the New dialog has parity.
  const categoriesApi = useApi((signal) => listTicketCategories(signal), []);
  const categoryOptions: TicketCategoryRow[] = categoriesApi.data?.categories ?? [];
  // Reusable ticket templates the user can start from (own + shared, 0108).
  const templatesApi = useApi((signal) => listTicketTemplates(signal), []);
  const templates = templatesApi.data?.templates ?? [];

  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [priority, setPriority] = useState("normal");
  // Legacy-parity intake fields.
  const [ticketType, setTicketType] = useState("service_request");
  // Campus → Building → Room share the Facilities cascade; all store codes.
  const [campus, setCampus] = useState("");
  const [building, setBuilding] = useState("");
  const [room, setRoom] = useState("");
  const [source, setSource] = useState("web");

  // Tenant-configured priorities; the seeded constants stand in while loading.
  // Once loaded, snap the selection to the tenant default if the current code
  // doesn't exist for this tenant.
  const prioritiesApi = useApi((signal) => listPriorities(signal), []);
  const priorityOptions: { code: string; label: string }[] =
    prioritiesApi.data?.priorities?.map((p) => ({ code: p.code, label: p.name })) ??
    TICKET_PRIORITIES;
  useEffect(() => {
    const rows = prioritiesApi.data?.priorities;
    if (!rows?.length) return;
    setPriority((cur) =>
      rows.some((p) => p.code === cur)
        ? cur
        : (rows.find((p) => p.is_default === 1) ?? rows[0]!).code,
    );
  }, [prioritiesApi.data]);
  // Tenant-configured "Submission Tracking" (source) options; the hardcoded
  // defaults stand in while loading. If the current pick vanishes, snap to first.
  const sourcesApi = useApi((signal) => getTicketSources(signal), []);
  const sourceOptions = sourcesApi.data?.sources ?? NEW_TICKET_SOURCES;
  useEffect(() => {
    const rows = sourcesApi.data?.sources;
    if (!rows?.length) return;
    setSource((cur) => (rows.some((s) => s.code === cur) ? cur : rows[0]!.code));
  }, [sourcesApi.data]);
  // Tenant-configured ticket types; seeded constants while loading. Snap to the
  // tenant default once loaded if the current code isn't one of them.
  const typesApi = useApi((signal) => listTicketTypes(signal), []);
  const ticketTypeOptions: { code: string; label: string }[] =
    typesApi.data?.ticket_types?.map((t) => ({ code: t.code, label: t.name })) ?? NEW_TICKET_TYPES;
  useEffect(() => {
    const rows = typesApi.data?.ticket_types;
    if (!rows?.length) return;
    setTicketType((cur) =>
      rows.some((t) => t.code === cur) ? cur : (rows.find((t) => t.is_default === 1) ?? rows[0]!).code,
    );
  }, [typesApi.data]);
  // Requesters + assignees mirror the edit-ticket window: multi-person chip
  // pickers with name/email autocomplete. Position 0 is the primary in each.
  const [requesters, setRequesters] = useState<RequesterPick[]>([]);
  const [assigneeRows, setAssigneeRows] = useState<AssigneePick[]>([]);
  const [department, setDepartment] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  // Intake notifications — optional, on by default.
  const [emailClient, setEmailClient] = useState(true);
  const [emailTech, setEmailTech] = useState(true);
  const { run, pending, error } = useMutation(createTicket);

  const busy = pending || uploading;

  function addFiles(list: FileList | null) {
    if (!list) return;
    // Snapshot the FileList NOW — the onChange resets the input's value right
    // after this call, which empties the live FileList. Reading it lazily inside
    // the setState updater would capture an emptied list (→ nothing attaches).
    const picked = Array.from(list);
    setFiles((prev) => [...prev, ...picked]);
  }
  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  // Choosing a category whose deepest node carries a default assignee auto-fills
  // the assignee — but only when none is set yet, so a manual pick always wins
  // (mirrors the edit window, 0089).
  function onCategoryChange(id: string | null) {
    setCategoryId(id);
    if (!id) return;
    const cat = categoryOptions.find((c) => c.id === id);
    if (cat?.default_assignee_id && assigneeRows.length === 0) {
      const a = assignees.find((x) => x.id === cat.default_assignee_id);
      if (a) setAssigneeRows([{ user_id: a.id, name: a.name }]);
    }
  }

  // Prefill the form from a saved template's settings (never touches the
  // assignee/requester — the template doesn't store them).
  function applyTemplate(id: string) {
    setTemplateId(id);
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    const d = tpl.data;
    if (d.subject != null) setSubject(d.subject);
    if (d.description != null) setDescription(d.description);
    if (d.priority_code) setPriority(d.priority_code);
    if (d.type) setTicketType(d.type);
    if (d.source) setSource(d.source);
    setDepartment(d.department ?? "");
    setCampus(d.campus ?? "");
    setBuilding(d.building ?? "");
    setRoom(d.room ?? "");
    onCategoryChange(d.category_id ?? null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!subject.trim()) return;
    const primaryReq = requesters[0];
    const primaryAssignee = assigneeRows[0];
    const res = await run({
      subject: subject.trim(),
      ...(richTextIsEmpty(description) ? {} : { description }),
      ...(priority ? { priority_code: priority } : {}),
      ...(primaryAssignee ? { assignee_id: primaryAssignee.user_id } : {}),
      ...(primaryReq?.name ? { requester_name: primaryReq.name } : {}),
      ...(primaryReq?.email ? { requester_email: primaryReq.email } : {}),
      ...(department.trim() ? { department: department.trim() } : {}),
      ...(categoryId ? { category_id: categoryId } : {}),
      ...(ticketType ? { type: ticketType } : {}),
      ...(campus ? { campus } : {}),
      ...(building ? { building } : {}),
      ...(room ? { room } : {}),
      ...(source ? { source } : {}),
      notify_requester: emailClient,
      notify_assignee: emailTech,
    });
    if (!res) return;
    // Persist the full requester/assignee sets when more than one was added
    // (create only carries the primary; the rest go via the same replace-set
    // PATCH the edit window uses). Best-effort — the ticket already exists.
    if (requesters.length > 1 || assigneeRows.length > 1) {
      try {
        await updateTicket(res.ticket.id, {
          ...(requesters.length > 1 ? { requesters: requesters.map((r) => ({ name: r.name, email: r.email })) } : {}),
          ...(assigneeRows.length > 1 ? { assignee_ids: assigneeRows.map((a) => a.user_id) } : {}),
        });
      } catch {
        /* land on the ticket; the user can finish setting people there */
      }
    }
    // Best-effort attachment upload after the ticket exists; failures don't lose
    // the ticket (the user lands on it and can re-attach).
    if (files.length) {
      setUploading(true);
      for (const f of files) {
        try {
          await uploadTicketAttachment(res.ticket.id, f);
        } catch {
          /* surface on the ticket page; don't block navigation */
        }
      }
      setUploading(false);
    }
    onCreated(res.ticket.id);
  }

  return (
    <Modal
      title="New ticket"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="create-ticket-form"
            disabled={busy || !subject.trim()}
          >
            {busy ? <Spinner label={uploading ? "Uploading…" : "Creating…"} /> : "Create ticket"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="create-ticket-form" onSubmit={onSubmit}>
        {templates.length > 0 ? (
          <label className="field" style={{ background: "var(--surface-2)", padding: 10, borderRadius: 8 }}>
            <span className="label">Start from template</span>
            <select value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">— none (blank ticket) —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.is_shared ? "" : " (private)"}
                </option>
              ))}
            </select>
            <span className="hint">Prefills the settings below — you can still change anything.</span>
          </label>
        ) : null}
        <label className="field">
          <span className="label">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Short summary of the request…"
            autoFocus
            required
          />
        </label>

        <div className="field">
          <span className="label">Description</span>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Add the details, steps, or context…"
          />
        </div>

        <div className="field">
          <span className="label">Ticket Type</span>
          <div className="seg" role="group" aria-label="Ticket type">
            {ticketTypeOptions.map((t) => (
              <button
                key={t.code}
                type="button"
                className={`seg-btn${ticketType === t.code ? " active" : ""}`}
                aria-pressed={ticketType === t.code}
                onClick={() => setTicketType(t.code)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <RequestTypePicker
          categories={categoryOptions}
          loading={categoriesApi.loading}
          currentId={categoryId}
          pending={busy}
          onSave={onCategoryChange}
        />

        <RequestersField requesters={requesters} onChange={setRequesters} disabled={busy} />

        <AssigneesField
          value={assigneeRows}
          options={assignees}
          loading={assigneesApi.loading}
          onChange={setAssigneeRows}
          disabled={busy}
        />

        <div className="row">
          <label className="field">
            <span className="label">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {priorityOptions.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
              {priority && !priorityOptions.some((p) => p.code === priority) ? (
                <option value={priority}>{priority}</option>
              ) : null}
            </select>
          </label>
        </div>

        <LocationPicker
          campus={campus}
          building={building}
          room={room}
          onChange={({ campus: c, building: b, room: r }) => {
            setCampus(c);
            setBuilding(b);
            setRoom(r);
          }}
          disabled={busy}
        />

        <div className="row">
          <label className="field">
            <span className="label">Department</span>
            <select value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="">—</option>
              {departmentOptions.map((d) => (
                <option key={d.id} value={d.code}>
                  {d.name}
                </option>
              ))}
            </select>
            {departmentsApi.loading ? <span className="hint">Loading departments…</span> : null}
          </label>
          <label className="field">
            <span className="label">Submission tracking</span>
            <select value={source} onChange={(e) => setSource(e.target.value)}>
              {sourceOptions.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="field">
          <span className="label">Attachments</span>
          <label className="filedrop">
            <input
              type="file"
              multiple
              className="sr-only"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <span>📎 Choose files…</span>
          </label>
          {files.length > 0 ? (
            <ul className="file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`}>
                  <span className="file-name">{f.name}</span>
                  <span className="faint">{Math.ceil(f.size / 1024)} KB</span>
                  <button
                    type="button"
                    className="file-x"
                    aria-label={`Remove ${f.name}`}
                    onClick={() => removeFile(i)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="field" style={{ marginBottom: 0 }}>
          <span className="label">Notifications</span>
          <div className="toggle-row">
            <label className="toggle" title="Email the requester the intake confirmation">
              <input type="checkbox" checked={emailClient} onChange={(e) => setEmailClient(e.target.checked)} />
              Email requester
            </label>
            <label className="toggle" title="Email the assignee their assignment">
              <input type="checkbox" checked={emailTech} onChange={(e) => setEmailTech(e.target.checked)} />
              Email assignee
            </label>
          </div>
        </div>
      </form>
    </Modal>
  );
}
